import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import type { AgentBrainSnapshot, RecentEvent, Task } from "../../lib/types";

const POLL_MS = 30_000;
const STALE_MS = 15_000;

function invalidateTaskModel(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["agent-queue"] });
  qc.invalidateQueries({ queryKey: ["agent-inbox"] });
  qc.invalidateQueries({ queryKey: ["needs-you"] });
  qc.invalidateQueries({ queryKey: ["agent-history"] });
  qc.invalidateQueries({ queryKey: ["agent-tasks"] });
  qc.invalidateQueries({ queryKey: ["workforce-status"] });
  qc.invalidateQueries({ queryKey: ["agent-brain-snapshot"] });
}

/**
 * Sprint 25.3 → 26.0 — the brain snapshot: one composite query backing the
 * whole agent page. Polls on the 30s cadence so the page stays live, and is
 * invalidated by every task-model mutation.
 */
export function useAgentBrainSnapshot(agentId: string | undefined, enabled = true) {
  return useQuery<AgentBrainSnapshot>({
    queryKey: ["agent-brain-snapshot", agentId],
    queryFn: () => mcpClient.getAgentBrainSnapshot(agentId as string),
    enabled: enabled && !!agentId,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/** The ranked queue (queued tasks, #1 = current). */
export function useAgentQueue(nodeId: string | undefined, enabled = true) {
  return useQuery<Task[]>({
    queryKey: ["agent-queue", nodeId],
    queryFn: () => (nodeId ? mcpClient.listTasks(nodeId) : []),
    enabled: enabled && !!nodeId,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/** Untriaged inbox tasks. */
export function useAgentInbox(nodeId: string | undefined, enabled = true) {
  return useQuery<Task[]>({
    queryKey: ["agent-inbox", nodeId],
    queryFn: () => (nodeId ? mcpClient.listTasks(nodeId, { status: "inbox", include_inbox: true }) : []),
    enabled: enabled && !!nodeId,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/**
 * Tasks whose attached workflow is waiting for a human.
 *
 * №22.6 (D-NY1) — with `nodeId` this is a PLACE read of one hire's desk: bounded
 * by the hire, so the server evaluates the scope contract but does not filter on
 * it (a hire's own work must not vanish from its own desk — D-CC4). Without
 * `nodeId` it is the AMBIENT home read and IS scoped. Callers here want the list;
 * the envelope's `scope` is read by the home, which is the surface that must
 * report what it withheld.
 */
export function useNeedsYou(nodeId?: string, enabled = true) {
  return useQuery<Task[]>({
    queryKey: ["needs-you", nodeId ?? "__all__"],
    queryFn: () => mcpClient.listNeedsYou(nodeId).then((f) => f.tasks),
    enabled,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/** Completed/dropped tasks — the agent's recent history. */
export function useAgentHistory(nodeId: string | undefined, enabled = true) {
  return useQuery<RecentEvent[]>({
    queryKey: ["agent-history", nodeId],
    queryFn: () => (nodeId ? mcpClient.getAgentBrainSnapshot(nodeId).then((s) => s.recent) : []),
    enabled: enabled && !!nodeId,
    staleTime: STALE_MS,
  });
}

/** Triage an inbox task into the ranked queue (inbox → queued). */
export function useTriageTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId }: { taskId: string }) => mcpClient.triageTask(taskId),
    onSuccess: () => invalidateTaskModel(qc),
  });
}

/** Make a task current — move it to rank #1. */
export function useMoveTaskToTop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId }: { taskId: string }) => mcpClient.moveTaskToTop(taskId),
    onSuccess: () => invalidateTaskModel(qc),
  });
}

export function useDropTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, reason }: { taskId: string; reason?: string }) =>
      mcpClient.dropTask(taskId, reason),
    onSuccess: () => invalidateTaskModel(qc),
  });
}
